import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { useRef } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

export default function GuidelinesPage() {
  const contentRef = useRef<HTMLDivElement>(null);
  
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const downloadGuidelines = async () => {
    if (!contentRef.current) return;
    
    try {
      // Create a new PDF document
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });
      
      // Add a heading to the PDF
      pdf.setFontSize(24);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'bold');
      pdf.text('2ByaaG Community Guidelines', 105, 20, { align: 'center' });
      
      // Add a subtitle
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text('To Bee Yaa (You) - A Community of Students, Parents, and Mentors', 105, 30, { align: 'center' });
      
      // Add a separator line
      pdf.setDrawColor(200, 200, 200);
      pdf.line(20, 35, 190, 35);
      
      // Capture the content
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Calculate dimensions to fit the content
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth() - 20; // Add margins
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      
      // Add the content image below the heading
      pdf.addImage(imgData, 'PNG', 10, 40, pdfWidth, pdfHeight);
      
      // Add page numbers if content spans multiple pages
      const pageCount = pdf.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Page ${i} of ${pageCount}`, 105, 287, { align: 'center' });
      }
      
      // Save the PDF
      pdf.save('2ByaaG-Community-Guidelines.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-orange-50 text-gray-800 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <motion.div {...fadeIn}>
          <div className="flex justify-between items-center mb-6">
            <Button variant="ghost" asChild>
              <Link to="/" className="flex items-center gap-2">
                <ArrowLeft size={20} />
                Back to Home
              </Link>
            </Button>
            <Button 
              onClick={downloadGuidelines}
              className="bg-yellow-600 hover:bg-yellow-700 text-white"
            >
              <Download className="mr-2 h-4 w-4" />
              Download Guidelines
            </Button>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-yellow-800 mb-8">
            2ByaaG Community Guidelines
          </h1>

          <div ref={contentRef} className="bg-white p-8 rounded-lg shadow-xl">
            <Card className="bg-white/80 backdrop-blur shadow-xl mb-8">
              <CardContent className="p-6">
                <p className="text-lg mb-6">
                  Welcome to 2ByaaG – To Bee Yaa (You)! Our community is a vibrant space where students, parents, and elderly mentors come together to explore career paths, share stories, and inspire one another.
                </p>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <motion.div {...fadeIn} className="space-y-6">
                <h2 className="text-2xl font-semibold text-yellow-700">1. Be Respectful and Kind</h2>
                <Card className="bg-white/80 backdrop-blur shadow-xl">
                  <CardContent className="p-6 space-y-3">
                    <p>Treat everyone with courtesy, regardless of age, background, or perspective. Value the diversity of experiences, from students' aspirations to mentors' wisdom.</p>
                    <p>Avoid personal attacks, insults, offensive language, harassment, or discrimination based on gender, religion, caste, ethnicity, or any other identity.</p>
                    <p>Disagree constructively, offering thoughtful feedback rather than negativity. For example, say, "I see it differently because…" instead of dismissing others' views.</p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div {...fadeIn} className="space-y-6">
                <h2 className="text-2xl font-semibold text-yellow-700">2. Share Relevant and Helpful Content</h2>
                <Card className="bg-white/80 backdrop-blur shadow-xl">
                  <CardContent className="p-6 space-y-3">
                    <p>Post content related to career exploration, education, or personal growth that aligns with 2ByaaG's mission. Examples include career questions, mentor stories, or advice for students.</p>
                    <p>Do not share spam, advertisements, misleading information, or content promoting illegal activities.</p>
                    <p>Respect intellectual property by crediting sources when sharing articles, images, or ideas.</p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div {...fadeIn} className="space-y-6">
                <h2 className="text-2xl font-semibold text-yellow-700">3. Engage Actively and Supportively</h2>
                <Card className="bg-white/80 backdrop-blur shadow-xl">
                  <CardContent className="p-6 space-y-3">
                    <p>Participate in discussions by sharing your experiences, asking questions, or offering advice. Your voice—whether as a student, parent, or mentor—adds value.</p>
                    <p>Support others by answering questions, celebrating successes, or providing encouragement.</p>
                    <p>Keep conversations on-topic to maintain focus. If you want to discuss something new, start a fresh thread or reach out to moderators for guidance.</p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div {...fadeIn} className="space-y-6">
                <h2 className="text-2xl font-semibold text-yellow-700">4. Protect Privacy and Safety</h2>
                <Card className="bg-white/80 backdrop-blur shadow-xl">
                  <CardContent className="p-6 space-y-3">
                    <p>Do not share personal information publicly or without consent, including your own or others'.</p>
                    <p>Respect the privacy of stories shared by mentors, students, or parents.</p>
                    <p>Report any concerning behavior to our moderators immediately for prompt action.</p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div {...fadeIn} className="space-y-6">
                <h2 className="text-2xl font-semibold text-yellow-700">5. Special Considerations</h2>
                <Card className="bg-white/80 backdrop-blur shadow-xl">
                  <CardContent className="p-6 space-y-3">
                    <h3 className="font-semibold">For Students:</h3>
                    <p>Feel free to ask questions, no matter how big or small. Mentors and peers are here to guide you.</p>
                    
                    <h3 className="font-semibold mt-4">For Parents:</h3>
                    <p>Engage with mentors' stories to understand career options. Your support is key.</p>
                    
                    <h3 className="font-semibold mt-4">For Mentors:</h3>
                    <p>Share your stories with enthusiasm, focusing on lessons that inspire and guide.</p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div {...fadeIn} className="space-y-6">
                <h2 className="text-2xl font-semibold text-yellow-700">Contact & Support</h2>
                <Card className="bg-white/80 backdrop-blur shadow-xl">
                  <CardContent className="p-6">
                    <p className="mb-4">If you see content or behavior that violates these guidelines, please report it:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Use the "Report" button on any post</li>
                      <li>Email us at support@2byaag.com</li>
                      <li>Contact a moderator directly</li>
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            <motion.div {...fadeIn} className="text-center py-12">
              <p className="text-xl font-semibold text-yellow-800">
                Together, let's build a supportive space where everyone feels valued, inspired, and empowered.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}